import { useCallback, useEffect, useState } from 'react';
import { api, silently } from '../lib/api';
import { DataTable, ErrorBox, Pager, loadAllPages, usePageSize, dash, money, useList, type Column, type Row } from '../components/DataTable';
import Modal from '../components/Modal';
// 편집 폼은 거래처별 정산과 **한 벌**을 같이 쓴다(2026-10-02) — 두 벌이면 상태 목록·저장 칸이 한쪽만 바뀐다.
import MisuEditForm, { REQ_STATUS, misuAccess } from '../components/MisuEditForm';
import { useMe } from '../lib/me';
import { useAutoSearch } from '../lib/search';
import MonthSearchHint from '../components/MonthSearchHint';
import DriveMonPicker from '../components/DriveMonPicker';
import SettleUnbilled from './SettleUnbilled';
import SettleMisuCancel from './SettleMisuCancel';

/**
 * 미수금 관리 — 레거시 `거래처정산관리 > 미수금관리`.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 이 화면의 두 가지 함정
 * ─────────────────────────────────────────────────────────────────────────
 * 🔴 **기간이 정산월이 아니라 운행시작일(`recv_startdt`)** 이다. 청구 목록(정산 > 거래처 청구)은
 *    정산월 기준이라 **같은 기간을 넣어도 건수가 다르다** — 실측 2026-08-01: 미수 12,205행 중
 *    **62.7%(7,687행)에서 두 앵커가 서로 다른 달**을 가리킨다. 레거시가 그렇게 자르므로 따른다.
 *    화면에도 적어 둔다(안 적으면 "왜 청구 목록과 안 맞지"에서 멈춘다).
 *
 * 🔴 **미수 = 입금일(`deposit_date`)이 비어 있는 건**(2026-08-01 사용자 확정).
 *    ⚠️ 처음엔 레거시 SP 대로 `req_status` 세 값으로 판정했는데 실측이 뒤집었다 —
 *    그 상태이면서 **입금일이 이미 찍힌 건이 11,653건**이다. `req_status` 는 **청구 진행 상태**라
 *    입금 후에도 '청구완료'로 남는다(같은 행의 `bank_status` 는 24,856건이 입금완료다).
 *    상태 기준 12,479건 vs 입금 기준 **1,243건** — 그 차이가 통째로 이미 받은 돈이었다.
 *    ⚠️ 입금일 없는 1,243건 중 108건은 입금상태가 '입금완료'다(2019년 이전, 입금일을 메모에만 적던 시절).
 *    정의를 그 예외로 흐리지 않고 **입금상태 칸을 같이 보여준다** — 눈에 띄면 담당자가 고친다.
 *
 * 금액은 저장돼 있지 않다. **부분입금 개념이 없어 미수액 = 청구액 전액**이다
 * (레거시에 입금액 컬럼이 DB 전체에 없다).
 */

/** 수금 축 — 화면 기본은 '미수만'. 축을 청구상태와 섞지 않는다(둘은 서로 다른 질문이다). */
const DEPOSIT_FILTER: { value: string; label: string }[] = [
  { value: '', label: '미수만 (입금 전)' },
  { value: '입금', label: '입금된 건' },
  { value: '전체', label: '전체' },
];

/** 오늘 기준 기본 기간 — 올해 1월 1일부터(2026-10-29 #434 이현숙 · 종전 작년 1월 1일). 전기간을 기본으로 열면 12,205건이 뜬다. */
function defaultRange(): { from: string; to: string } {
  const now = new Date();
  const to = `${now.getFullYear()}-12-31`;
  const from = `${now.getFullYear()}-01-01`;
  return { from, to };
}

/**
 * 미수금 탭 = 보기 둘(2026-10-02 way). **미수**(청구 후 입금 전 · 원장 줄 기준 · 운행시작일 축)와
 * **미청구**(거래처별 정산엔 있는데 청구가 안 걸린 접수 · 정산월 축 — `SettleUnbilled.tsx`).
 * 축이 서로 달라 한 표에 섞지 않는다.
 */
export default function SettleMisu({
  months,
  onOpenCust,
  initialView,
}: {
  months: readonly string[];
  onOpenCust?: (recvId: number, driveMon: string) => void;
  /** 링크로 들어온 보기(`?tab=misu&view=cancel` — 구글챗 「청구 취소 요청」 알림 버튼). */
  initialView?: 'cancel';
}) {
  const [view, setView] = useState<'misu' | 'unbilled' | 'cancel'>(initialView ?? 'misu');
  useEffect(() => { if (initialView) setView(initialView); }, [initialView]);
  const { me } = useMe();
  /** 청구 취소 승인요청(10-02) — 회계 입력·관리자에게만 「취소요청 N」 칸. 위칸을 넓히지 않고 보기 전환에 붙인다. */
  const canDecide = misuAccess(me, 'misu');
  const [pending, setPending] = useState(0);
  const loadPending = useCallback(() => {
    if (!canDecide) return;
    // 배지용 — 로딩 카드를 띄우지 않는다.
    silently(() => {
      api.listMisuCancelRequests({ status: '대기' }).then((r) => setPending(r.pending)).catch(() => undefined);
    });
  }, [canDecide]);
  useEffect(() => { loadPending(); }, [loadPending]);
  // 🔴 위칸은 **한 줄**(2026-10-02 way 「미수금 화면 상단이 너무 넓은데 최대로 최적화 하고 아래 리스트가 잘 보이도록」 —
  //    09-30 「위칸을 최대로 좁히고 아래 작업칸을 넓게」와 같은 원칙). 제목·설명 단락은 없애고 ⓘ 툴팁으로,
  //    보기 전환·필터는 PC 한 줄 · 폰은 줄바꿈.
  const toggle = (
    <div className="flex shrink-0 items-center gap-1">
      <div className="flex gap-0.5 rounded-lg bg-canvas p-0.5 text-sm">
        {([
          ['misu', '미수', '청구 후 입금 전(입금일 미입력) · 기간 = 운행 시작일'],
          ['unbilled', '미청구', '거래처별 정산엔 있는데 청구가 안 걸린 접수 · 정산월 기준'],
          ...(canDecide ? [['cancel', pending > 0 ? `취소·수정 ${pending}` : '취소·수정', '영업이 올린 청구 취소·수정 요청 결과(조회) — 승인·반려는 회계 › 발행 요청'] as const] : []),
        ] as const).map(([id, label, tip]) => (
          <button
            key={id}
            type="button"
            title={tip}
            onClick={() => setView(id)}
            className={`whitespace-nowrap rounded-md px-2.5 py-1 font-medium ${view === id ? 'bg-surface text-brand-700 shadow-sm' : id === 'cancel' && pending > 0 ? 'text-red-700' : 'text-ink-soft'}`}
          >
            {label}
          </button>
        ))}
      </div>
      <span className="cursor-help text-xs text-ink-faint" title={view === 'misu' ? MISU_TIP : view === 'cancel' ? CANCEL_TIP : UNBILLED_TIP}>ⓘ</span>
    </div>
  );
  if (view === 'cancel' && canDecide) return <SettleMisuCancel toggle={toggle} />;
  if (view === 'unbilled') return <SettleUnbilled months={months} onOpenCust={onOpenCust} toggle={toggle} />;
  return <MisuList months={months} toggle={toggle} />;
}

/** 미수 보기 설명 — 예전 위칸 단락 두 개를 툴팁 하나로(2026-10-02). */
const MISU_TIP = [
  '미수 = 입금일이 아직 입력되지 않은 건입니다. 청구상태(청구완료 등)는 청구 진행 상태라 입금 후에도 남아 미수 판정에 쓰지 않습니다.',
  '기간은 운행 시작일 기준입니다(거래처별 정산은 정산월 기준이라 같은 기간이어도 건수가 다릅니다). 정산월을 고르면 기간이 비워집니다.',
  '부분입금 개념이 없어 미수액 = 청구액 전액입니다.',
  '청구완료·입금상태·입금일 입력은 회계팀(회계 입력 권한)이 합니다 — 청구대상은 영업이 거래처별 정산에서 「청구 요청」을 올리면 걸립니다.',
].join('\n');
const CANCEL_TIP = [
  '청구 요청 뒤 영업이 올린 「취소」·「수정」 요청의 결과입니다(10-19 일원화 — 미수는 결과만).',
  '처리(승인·반려)는 회계 › 발행 요청 화면의 「취소」·「수정」 칩에서 합니다 — 승인하면 계산서 수정(마이너스) 발행과 청구상태 초기화가 한 번에 됩니다.',
  '취소 = 청구 요청 전으로(입금상태·입금일·미수번호·메모는 그대로) · 수정 = 바뀐 금액으로 발행 요청을 다시 엽니다.',
].join('\n');
const UNBILLED_TIP = [
  '거래처별 정산에 있는 접수 중 청구 기록이 없거나 청구상태가 비어 있고(영업 청구 요청 전) 입금 표시도 없는 건입니다(정산월 기준).',
  '「공제 대기」가 있는 줄은 청구 전이라 기사 명세와 OPS 선지급에 반영되지 않습니다 — 영업에게 「청구 요청」을 올려 달라고 해 주세요.',
].join('\n');

function MisuList({ months, toggle }: { months: readonly string[]; toggle: React.ReactNode }) {
  const { me } = useMe();
  /** 전체 편집 = 회계 화면 입력·관리자(서버 saveResultMisu scope 'misu'). 그 밖은 줄을 눌러 보기만. */
  const canEdit = misuAccess(me, 'misu');

  const init = defaultRange();
  const [from, setFrom] = useState(init.from);
  const [to, setTo] = useState(init.to);
  const [deposit, setDeposit] = useState('');
  /**
   * 정산년월 축(2026-08-01 사용자 요청). ⚠️ 기간(운행시작일)과 **서로 다른 축**이다 —
   * 실측 62.7% 에서 두 앵커가 다른 달을 가리키므로 **둘을 같이 걸면 0건이 뜬다.**
   * 그래서 한쪽을 고르면 다른 쪽을 비운다(화면에도 그 사실을 적는다).
   */
  const [driveMon, setDriveMon] = useState('');
  /** 정산년월로 묶어 소계 보기. 켜면 서버가 **정렬까지 그 키로** 바꾼다(안 바꾸면 소계가 흩어진다). */
  const [groupByMon, setGroupByMon] = useState(false);
  const [status, setStatus] = useState('');
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [PAGE, setPAGE] = usePageSize('settle-misu', 50);

  // 치는 대로 찾는다(입력이 멎으면 적용). 버튼·Enter 는 그대로 — `lib/search.ts` 참고.
  useAutoSearch(term, (v) => { setQ(v); setPage(0); });
  const [nonce, setNonce] = useState(0);
  const [edit, setEdit] = useState<Row | null>(null);

  /** 목록에 못 실리는 미수(접수 삭제·부재). 조용히 빼면 "목록에 없는 미수"가 된다. */
  const [orphan, setOrphan] = useState<{ count: number; no_recv: number; deleted_recv: number } | null>(null);

  const { rows, loading, error, total, subtotals } = useList(
    async () => {
      const r = await api.listResultMisu({
        q: q || undefined,
        from: from || undefined,
        to: to || undefined,
        deposit,
        reqStatus: status,
        driveMon: driveMon || undefined,
        groupByMon,
        limit: PAGE,
        offset: page * PAGE,
      });
      setOrphan(r.orphan);
      return r as unknown as { rows: Row[] };
    },
    [q, from, to, deposit, status, driveMon, groupByMon, page, PAGE, nonce],
  );

  const columns: Column[] = [
    // 접수 NO = 레거시 RecvId 와 같은 번호(배차·거래처 청구 화면과 같은 값). 검색창에 숫자만 치면 찾는다.
    // phone: 폰에선 거래처를 왼쪽에 고정하고 번호·청구일·청구요청일·담당·변경 칸은 뺀다(DataTable `phone` · 모바일 7차).
    { key: 'recv_id', label: '접수 NO', align: 'right', phone: 'hide', render: (r) => <span className="text-xs text-ink-soft">{dash(r.recv_id)}</span> },
    { key: 'recv_startdt', label: '운행 시작', render: (r) => dash(r.recv_startdt) },
    { key: 'cust_name', label: '거래처', phone: 'pin' },
    // 정산년월 — 이 화면의 **두 번째 축**이다(기간은 운행시작일). 소계도 이 키로 붙는다.
    { key: 'drive_mon', label: '정산월', render: (r) => dash(r.drive_mon) },
    {
      key: 'req_status',
      label: '청구상태',
      render: (r) => {
        const st = String(r.req_status ?? '').trim();
        if (st === '') return <span className="text-ink-faint">—</span>;
        // 색은 **수금**(입금일)으로 정한다 — 청구상태는 입금 후에도 '청구완료'로 남는다.
        const misu = String(r.deposit_date ?? '').trim() === '';
        return (
          <span
            className={`rounded px-1.5 py-0.5 text-xs font-medium ${
              misu ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'
            }`}
          >
            {st}
          </span>
        );
      },
    },
    { key: 'bank_status', label: '입금상태' },
    { key: 'req_date', label: '청구일', phone: 'hide' },
    {
      key: 'deposit_date',
      label: '입금일',
      // 미수 판정의 **정본 칸**이다 — 비어 있으면 그게 곧 미수다.
      render: (r) =>
        String(r.deposit_date ?? '').trim() === ''
          ? <span className="font-medium text-red-700">미입력</span>
          : String(r.deposit_date),
    },
    { key: 'req_call_date', label: '청구요청일', phone: 'hide' },
    {
      key: 'cost_cust_total',
      label: '미수액',
      align: 'right',
      // 부분입금이 없으므로 청구액 전액이 곧 미수액이다.
      render: (r) => <b>{money(r.cost_cust_total)}</b>,
    },
    { key: 'fin_manager', label: '거래처정산담당자', phone: 'hide' },
    { key: 'misu_comment', label: '메모' },
    {
      key: 'updated_by',
      label: '변경',
      phone: 'hide',
      render: (r) => (
        <span className="text-xs text-ink-faint" title={String(r.updated_at ?? '')}>
          {dash(r.updated_by)}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-2">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setQ(term.trim());
          setPage(0);
        }}
        className="btn-row-sm flex flex-wrap items-center gap-x-2 gap-y-1.5"
      >
        {toggle}
        {/* 라벨 줄을 없앴다 — 칸 이름은 title·첫 선택지로. 운행 시작일 ↔ 정산월은 서로를 비운다(62.7% 가 다른 달). */}
        <span className="flex items-center gap-1" title="운행 시작일 기간 — 정산월을 고르면 비워집니다">
          <input type="date" aria-label="운행 시작일부터" className="field w-[8.5rem] px-2" value={from}
            onChange={(e) => { setFrom(e.target.value); if (e.target.value) setDriveMon(''); setPage(0); }} />
          <span className="text-ink-faint">~</span>
          <input type="date" aria-label="운행 시작일까지" className="field w-[8.5rem] px-2" value={to}
            onChange={(e) => { setTo(e.target.value); if (e.target.value) setDriveMon(''); setPage(0); }} />
        </span>
        {/* 🔴 DriveMonPicker 를 `<label>` 로 감싸지 말 것 — 자기 label 을 그려 드롭다운이 열리자마자 닫힌다(2026-08-01). */}
        <DriveMonPicker
          months={months}
          value={driveMon}
          width="w-28"
          extra={{ value: '', label: '전체' }}
          onChange={(m) => {
            setDriveMon(m);
            // 축이 둘이면 서로를 지운다 — 62.7% 가 어긋나 함께 걸면 0건이 된다.
            if (m) { setFrom(''); setTo(''); }
            setPage(0);
          }}
        />
        <select className="field w-auto" aria-label="수금" title="수금" value={deposit} onChange={(e) => { setDeposit(e.target.value); setPage(0); }}>
          {DEPOSIT_FILTER.map((d) => (
            <option key={d.value} value={d.value}>{d.label}</option>
          ))}
        </select>
        <select className="field w-auto" aria-label="청구상태" title="청구상태" value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }}>
          <option value="">청구상태 전체</option>
          {REQ_STATUS.filter((s) => s !== '').map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <span className="flex min-w-0 flex-1 items-center gap-2 md:flex-none">
          <input
            className="field min-w-0 flex-1 md:w-52 md:flex-none"
            placeholder="거래처 · 담당자 이름 · 메모 · 미수번호 · 접수 NO"
            title="거래처 · 거래처정산담당자 · 버스인접수담당자 · 메모 · 미수번호 · 접수 NO"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />
          <button className="btn-search" type="submit" aria-label="검색" title="검색" />
        </span>
        <label className="flex items-center gap-1 whitespace-nowrap text-xs text-ink-soft" title="켜면 목록이 정산년월 순서로 다시 정렬됩니다">
          <input type="checkbox" checked={groupByMon} onChange={(e) => { setGroupByMon(e.target.checked); setPage(0); }} />
          정산월 소계
        </label>
        {/* 조용히 빼면 "목록에 없는 미수"가 된다 — 수는 남기되 한 줄 크기로(2026-10-02). */}
        {orphan && orphan.count > 0 && (
          <span
            className="cursor-help whitespace-nowrap text-xs text-amber-800"
            title={`이 목록에 실리지 않는 미수 ${orphan.count.toLocaleString('ko-KR')}건 — 접수 정보가 없는 ${orphan.no_recv.toLocaleString('ko-KR')}건, 접수가 삭제된 ${orphan.deleted_recv.toLocaleString('ko-KR')}건.\n기간이 운행 시작일 기준인데 그 값이 없어 어떤 기간으로도 찾을 수 없습니다(레거시 화면에서도 안 보이던 건입니다).`}
          >
            ⚠ 목록 밖 {orphan.count.toLocaleString('ko-KR')}건
          </span>
        )}
        {!canEdit && <span className="whitespace-nowrap text-xs text-ink-faint" title="청구·입금 상태 입력은 회계 입력 권한이 필요합니다">보기 전용</span>}
      </form>

      <MonthSearchHint
        q={q}
        mon={driveMon}
        months={months}
        onPick={(m) => { setDriveMon(m); setFrom(''); setTo(''); setPage(0); }}
        onAll={() => { setDriveMon(''); setPage(0); }}
      />

      {error && <ErrorBox message={error} />}

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        total={total}
        subtotals={groupByMon ? subtotals : null}
        groupOf={(r) => String(r.drive_mon ?? '').trim() || '(정산월 없음)'}
        totalAt="top"
        tableId="settle.misu"
        exportXlsx={{
          name: `미수금${driveMon ? `_${driveMon}` : ''}`,
          loadAll: () => loadAllPages((limit, offset) =>
            api.listResultMisu({
              q: q || undefined, from: from || undefined, to: to || undefined, deposit, reqStatus: status,
              driveMon: driveMon || undefined, groupByMon, limit, offset,
            }).then((r) => (r as unknown as { rows: Row[] }).rows)),
        }}
        // 누구나 눌러 본다 — 회계 입력이면 편집, 아니면 보기 전용 창(way 「청구상태 모든 정보 조회 가능」).
        onRowClick={setEdit}
        activeRow={(r) => edit?.result_misu_id === r.result_misu_id}
      />
      <Pager page={page} setPage={setPage} rowCount={rows.length} pageSize={PAGE} loading={loading} setPageSize={setPAGE} baseSize={50} />

      {edit && (
        <Modal>
          <MisuEditForm
            row={edit}
            scope="misu"
            onClose={() => setEdit(null)}
            onSaved={() => { setEdit(null); setNonce((n) => n + 1); }}
          />
        </Modal>
      )}
    </div>
  );
}
